import styled from '@emotion/styled';
import { Select, Form } from 'antd';
import FormField from 'components/FormField';
import HintTextarea from 'components/HintTextarea';
import { Formik, FormikConfig, FormikProps, useFormikContext } from 'formik';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { ReactNode, useCallback, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { Image } from 'views/Management/Feedback/components/FeedbackTable';
import useCaseCategory, { CategoryMap } from 'views/Management/Feedback/hooks/useCaseCategory';

export type FeedbackFormValues = Util.PartialPartial<
  Feedback.ReportCase,
  'caseCategory' | 'reason'
>;

type FV = FeedbackFormValues;

export interface FeedbackFromProps extends FormikConfig<FV> {
  children: (form: ReactNode, props: FormikProps<FV>) => ReactNode;
  storeName?: string;
}

const reasonLimit = 100;

function FeedbackForm(props: FeedbackFromProps) {
  const { children, storeName, ...otherProps } = props;

  const { t } = useTranslate();

  const { loading: categoryListLoading, list: categoryList, categoryMap } = useCaseCategory();

  const validate = useCallback(
    (values: FV) => {
      const errors: Partial<Record<keyof FV, string>> = {};

      const { caseType, caseCategory, reason } = values;

      if (!caseCategory) {
        errors.caseCategory = t('feedback.请选择问题分类');
      }

      const caseMap = caseType && caseType !== 'ACTIVITY' ? categoryMap[caseType] : undefined;

      const reasonRequire = caseCategory && caseMap?.[caseCategory]?.requireReason;

      if (reasonRequire && (!reason || !reason.trim())) {
        errors.reason = t('feedback.请输入问题描述');
      } else if (reason && reason.length > reasonLimit) {
        errors.reason = t('feedback.请输入x字以内的问题描述', { x: reasonLimit });
      }

      return errors;
    },
    [categoryMap, t],
  );

  return (
    <Formik enableReinitialize validate={validate} {...otherProps}>
      {(props) => {
        const form = (
          <FormContent
            categoryList={categoryList || []}
            categoryListLoading={categoryListLoading}
            categoryMap={categoryMap}
            storeName={storeName}
          />
        );

        return children(form, props);
      }}
    </Formik>
  );
}

const MessagePreview = styled.div`
  position: relative;
  padding: 8px 8px 8px 16px;
  color: #84868c;
  background: #f7f9fa;
  border-radius: 2px;
  > p {
    margin: 0;
  }
  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 8px;
    left: 8px;
    width: 2px;
    height: calc(100% - 16px);
    background: #e1e4eb;
  }
`;

const CategorySelect = styled.div`
  width: 260px;
`;

const ReasonInput = styled.div`
  width: 400px;
`;

export interface FormContentProps {
  categoryList: Feedback.CaseCategory[];
  categoryListLoading: boolean;
  categoryMap: CategoryMap;
  storeName?: string;
}

function FormContent(props: FormContentProps) {
  const { categoryList, storeName, categoryListLoading, categoryMap } = props;

  const { t } = useTranslate();

  const { values } = useFormikContext<FV>();

  const { caseType, caseCategory, message } = values;

  const { fromUserName, msgFromType } = message || {};

  const isRobotMessage = msgFromType === 'BOT';
  const showName = isRobotMessage ? `${storeName}-${t('ViewsChat.ai.reply.flag')}` : fromUserName;

  const messagePreview = message ? (
    <Form.Item>
      <MessagePreviewer message={message} showName={showName} />
    </Form.Item>
  ) : null;

  const systemLanguage = useSystemLanguageValue();

  const categoryOptions = useMemo(() => {
    return categoryList.reduce<{ value: string; label: string }[]>((res, category) => {
      const { type, label: langLabelMap, caseType: currentCaseType } = category;

      const { [systemLanguage]: label } = langLabelMap;

      if (currentCaseType === caseType) {
        res.push({
          value: type,
          label,
        });
      }

      return res;
    }, []);
  }, [categoryList, caseType, systemLanguage]);

  const categorySelect = (
    <FormField<FV['caseCategory']> name="caseCategory" label={t('feedback.问题分类')} required>
      {({ field }) => {
        return (
          <CategorySelect>
            <Select
              placeholder={t('feedback.请选择问题分类')}
              loading={categoryListLoading}
              options={categoryOptions}
              getPopupContainer={(trigger) => trigger}
              {...field}
            />
          </CategorySelect>
        );
      }}
    </FormField>
  );

  const currentCategory =
    caseType && caseType !== 'ACTIVITY' && caseCategory
      ? categoryMap[caseType]?.[caseCategory]
      : undefined;

  const reasonInput = currentCategory ? (
    <FormField<FV['reason']>
      name="reason"
      label={t('feedback.问题描述')}
      required={currentCategory.requireReason}
    >
      {({ field }) => {
        return (
          <ReasonInput>
            <HintTextarea placeholder={t('feedback.请输入问题描述')} limit={100} {...field} />
          </ReasonInput>
        );
      }}
    </FormField>
  ) : null;

  return (
    <Form layout="vertical">
      {messagePreview}
      {categorySelect}
      {reasonInput}
    </Form>
  );
}

export function MessagePreviewer({
  message,
  showName,
}: {
  message: Conversation.Message;
  showName: ReactNode;
}) {
  const { t } = useTranslate();

  const { messageType, textContent, imageContent, imageWithTextContent } = message;

  const messageTypeMap: Partial<Record<Conversation.MessageType, ReactNode>> = {
    TEXT: <p>{textContent?.originalText || textContent?.text}</p>,
    IMAGE_WITH_TEXT: <p>{`[${t('messageType.IMAGE')}]${imageWithTextContent?.text}`}</p>,
    IMAGE: <Image src={imageContent?.imgUrl || imageContent?.externalImgUrl} alt="messageImage" />,
  };

  const { [messageType]: messageContent = <p>[{t(`messageType.${messageType}`)}]</p> } =
    messageTypeMap;

  return (
    <MessagePreview>
      <div>{showName}:</div>
      {messageContent}
    </MessagePreview>
  );
}

export default FeedbackForm;
